import { Select, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getPopups } from '../../../api/popup.api';

import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import Header from '../../../components/layout/Header';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import SelectCampus from '../../../components/SelectCampus';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import { IGetPopups, IPopupItem } from '../../../models/popup.model';
import { setCurrentCampus } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import useUtil from '../../../hooks/useUtil';

const HomepagePopup = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { isCampusDeactived } = useUtil();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });

  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { data: res, isLoading, error, execute } = useAxios<IGetPopups>();

  const [field, setField] = useState<string>('title');
  const [keyword, setKeyword] = useState<string>('');
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});
  const [campusDeactived, setCampusDeactived] = useState(false);

  const homepagePopupUrl = '/homepage/popup';

  useEffect(() => {
    setCampusDeactived(isCampusDeactived());
  }, [isCampusDeactived]);

  useEffect(() => {
    if (currentCampus) {
      const _param: any = {
        pageNo: page,
        perPage: config.pageSize,
      };

      if (query.campusCode) {
        dispatch(setCurrentCampus(String(query.campusCode)));
        _param.campusCode = String(query.campusCode);
      }
      if (query.page) {
        _param.pageNo = Number(query.page);
        setPage(Number(query.page));
      }
      if (query.title) {
        _param.title = String(query.title);
        setField('title');
        setKeyword(String(query.title));
      }

      execute(getPopups(currentCampus, _param));
      _param.page = _param.pageNo;
      delete _param.pageNo;
      delete _param.perPage;
      setParam(_param);
    }
  }, [currentCampus, location]);

  const getCampusName = (campusCode: string) => {
    const campus = campusData.find((item) => item.campusCode === campusCode);
    return campus?.campusKorName || campusCode;
  };

  const onSearch = () => {
    const param: any = { page: 1 };
    param.campusCode = currentCampus;
    switch (field) {
      case 'title':
        param.title = keyword;
        break;
      case 'regMemName':
        param.regMemName = keyword;
        break;
    }

    history.push(`${homepagePopupUrl}?${QueryString.stringify(param)}`);
  };

  const columns: TableColumnsType<IPopupItem> = [
    {
      title: t('homepage.archive.column1'),
      dataIndex: 'popupCode',
      key: 'popupCode',
      align: 'center',
      sorter: (a, b) => a.popupCode - b.popupCode,
      render: (value, record) => {
        return value;
      },
    },
    {
      title: t('system.code.column4'),
      dataIndex: 'openYn',
      key: 'openYn',
      align: 'center',
      sorter: (a, b) => a.openYn?.localeCompare(b.openYn),
      render: (value) => (value === 'Y' ? t('common.text.public') : t('common.text.private')),
    },
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusCode',
      key: 'campusCode',
      align: 'center',
      sorter: (a, b) => a.campusCode?.localeCompare(b.campusCode),
      render: (value) => getCampusName(value),
    },
    {
      title: t('common.column.title'),
      dataIndex: 'title',
      key: 'title',
      align: 'center',
      sorter: (a, b) => a.title?.localeCompare(b.title),
    },
    {
      title: t('homepage.archive.column5'),
      dataIndex: 'regDate',
      key: 'regDate',
      align: 'center',
      sorter: (a, b) => a.firstRegDttm?.localeCompare(b.firstRegDttm),
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('homepage.archive.column6'),
      dataIndex: 'memberName',
      key: 'regMemKorName',
      align: 'center',
      sorter: (a, b) => a.regMemKorName?.localeCompare(b.regMemKorName),
    }
  ];

  return (
    <div className="content-wrapper">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <Header
        iconFile="icon-homepage-big.svg"
        parentMenu={t('common.menu.homepage')}
        currentMenu={t('homepage.popup.text1')}
      />

      <div className="main">
        <div className="flex flex-justify-center">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="w-150p mr-2">
                  <SelectCampus />
                </div>
                <div className="w-150p mr-2">
                  <Select
                    className="w-100"
                    placeholder={t('common.button.select')}
                    value={field}
                    onChange={(value) => setField(value)}
                  >
                    <Select.Option value="title">{t('common.column.title')}</Select.Option>
                  </Select>
                </div>
                <input
                  type="text"
                  className="form-control w-240p mr-2"
                  placeholder={t('homepage.notice.placeholder1')}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') onSearch();
                  }}
                />
                <Button
                  className="btn btn-secondary btn-search btn-w100p"
                  onClick={onSearch}
                  text={t('common.button.search')}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-6 pr-t20">{t('common.text.count')}: {res?.total}{t('common.text.unit')}</div>
          <div className="col-6 text-right">
            <Button
              className="btn btn-primary btn-w100p"
              onClick={() => history.push(`${homepagePopupUrl}/create`)}
              text={t('event.education.button1')}
              disabled={campusDeactived}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div>
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `${homepagePopupUrl}/${record.campusCode}/${record.popupCode}${location.search}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="popupCode"
          />
        </div>
        <div className="row">
          <div className="col-3" />
          <div className="col-6">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={param}
              path={homepagePopupUrl}
            />
          </div>
          <div className="col-3 text-right">
            <Button
              className="btn btn-primary btn-w100p mr-2"
              onClick={() => history.push(`${homepagePopupUrl}/create`)}
              text={t('event.education.button1')}
              disabled={campusDeactived}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomepagePopup;
